import {
  Timeline,
  TimelineItem,
  useMedplum,
  useMedplumNavigate,
  useSearch,
} from '@medplum/react';
import { FC, useEffect, useState } from 'react';
import { DocumentReference } from '@medplum/fhirtypes';
import { usePatient } from '@hooks/usePatient';
import {
  Container,
  Title,
  Text,
  Image,
  Grid,
  Center,
  Modal,
  Button,
  Group,
} from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { IconPlus } from '@tabler/icons-react';

const getImageIdsForObservation = (observation: any): string[] => {
  if (!observation?.derivedFrom) {
    return [];
  }

  return observation.derivedFrom?.map(
    (derivedFrom: any) => derivedFrom.reference
  );
};

// -----------------------------------------------------------------------------
export const PatientVisitHistory: FC = () => {
  const patient = usePatient();
  const [images, setImages] = useState<DocumentReference[]>([]);
  const [focusedImage, setFocusedImage] = useState<DocumentReference | null>(
    null
  );

  // ---------------------------------------------------------------------------
  const medplum = useMedplum();
  const navigate = useMedplumNavigate();
  const profile = medplum.getProfile();

  // ---------------------------------------------------------------------------
  const [observations] = useSearch('Observation', {
    subject: `Patient/${patient?.id}`,
  });

  // ---------------------------------------------------------------------------
  useEffect(() => {
    const loadImages = async (): Promise<void> => {
      if (!observations?.entry) {
        return;
      }

      const imageIds = observations.entry
        .map((entry: any) => entry.resource.derivedFrom)
        .flat()
        .filter((derivedFrom: any) => derivedFrom?.reference)
        .map((derivedFrom: any) => derivedFrom.reference);

      const images = await medplum.searchResources('DocumentReference', {
        _id: imageIds.join(','),
      });

      setImages(images);
    };

    loadImages();
  }, [observations, medplum]);

  // ---------------------------------------------------------------------------
  if (!patient || !profile) {
    return null;
  }

  if (!observations) {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <Container>
      <Modal
        opened={focusedImage !== null}
        size="75%"
        onClose={() => setFocusedImage(null)}
        withCloseButton={false}
        centered
      >
        <Center>
          <Image
            radius="sm"
            key={focusedImage?.id}
            src={focusedImage?.content?.[0]?.attachment?.url}
            fit="contain"
            h="auto"
            maw="70vw"
            w="auto"
            fallbackSrc="https://placehold.co/600x400?text=Placeholder"
          />
        </Center>
      </Modal>

      <Timeline>
        <Group mt="lg" justify="end">
          <Button onClick={() => navigate(`${window.location.pathname}/new`)}>
            <Group gap="xs">
              <Text>New Visit</Text>
              <IconPlus size={16} />
            </Group>
          </Button>
        </Group>
        {observations?.entry
          ?.sort(
            (a, b) =>
              new Date(b.resource?.meta?.lastUpdated || 0).getTime() -
              new Date(a.resource?.meta?.lastUpdated || 0).getTime()
          )
          .map((obs) => {
            const observation = obs.resource;
            if (!observation) {
              return null;
            }

            const observationImagesIds = getImageIdsForObservation(observation);
            const observationImages = images.filter((image) => {
              if (!image?.id) {
                return false;
              }

              const referenceString = getReferenceString(image);
              if (!referenceString) {
                return false;
              }

              return observationImagesIds.includes(referenceString);
            });

            return (
              <TimelineItem resource={observation}>
                {observation?.note && observation?.note.length > 0 && (
                  <Container mb="lg">
                    <Title order={3}>Notes</Title>
                    {observation.note.map((note: any) => {
                      return <Text size="lg">{note.text}</Text>;
                    })}
                  </Container>
                )}

                {observationImages?.length > 0 && (
                  <Container mb="lg">
                    <Title order={3}>Images</Title>
                    <Grid>
                      {observationImages.map(
                        (documentReference: DocumentReference) => {
                          return (
                            <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
                              <Center>
                                <Image
                                  radius="md"
                                  key={documentReference.id}
                                  src={
                                    documentReference.content?.[0]?.attachment
                                      ?.url
                                  }
                                  h={150}
                                  fit="cover"
                                  w="250"
                                  fallbackSrc="https://placehold.co/600x400?text=Placeholder"
                                  onClick={() =>
                                    setFocusedImage(documentReference)
                                  }
                                />
                              </Center>
                            </Grid.Col>
                          );
                        }
                      )}
                    </Grid>
                  </Container>
                )}
              </TimelineItem>
            );
          })}
      </Timeline>
    </Container>
  );
};
